<template>
  <div class="correlation-graph-card h-full">
    <div class="relative h-full overflow-hidden">
      <!-- 空状态 -->
      <div
        v-if="showEmptyState"
        class="empty-state h-96 flex items-center justify-center border border-gray-200 rounded-lg bg-white"
      >
        <div class="text-center">
          <div class="relative mb-4">
            <div
              class="relative mx-auto h-12 w-12 flex items-center justify-center rounded-full from-green-500 to-blue-600 bg-gradient-to-r shadow-lg"
            >
              <icon-vue icon="mdi:graph-outline" class="text-xl text-white" :class="{ 'animate-pulse': isWaiting }" />
              <!-- 波纹效果 -->
              <div
                v-if="isWaiting"
                class="absolute inset-0 animate-ping border-2 border-green-300 rounded-full opacity-60"
                style="animation-duration: 3s"
              ></div>
              <div
                v-if="isWaiting"
                class="absolute inset-0 animate-ping border-2 border-blue-300 rounded-full opacity-40"
                style="animation-delay: 1s; animation-duration: 3s"
              ></div>
            </div>
          </div>
          <h3 class="mb-2 text-sm font-medium text-gray-700">智能关联分析</h3>
          <p class="mb-4 text-xs leading-relaxed text-gray-500">
            AI将分析代码与测试的关联关系
            <br />
            生成
            <span class="font-medium text-green-600">可视化关联图谱</span>
          </p>
          <div class="flex items-center justify-center text-xs text-gray-400 space-x-2">
            <span class="h-2 w-2 rounded-full bg-green-500" :class="{ 'animate-pulse': isWaiting }"></span>
            <span>{{ isWaiting ? '等待分析启动' : '分析完成' }}</span>
          </div>
        </div>
      </div>

      <!-- 加载覆盖层 -->
      <div
        v-if="isLoading"
        class="loading-overlay absolute inset-0 z-10 flex items-center justify-center overflow-hidden rounded-lg bg-white bg-opacity-90"
        :style="{ height: chartHeight, maxHeight: chartHeight }"
      >
        <div class="text-center">
          <div class="relative">
            <icon-vue icon="mdi:loading" class="mx-auto mb-4 h-12 w-12 animate-spin text-blue-600" />
            <div class="absolute h-4 w-4 animate-bounce rounded-full bg-green-500 -right-1 -top-1"></div>
          </div>
          <div class="space-y-2">
            <p class="text-sm font-medium text-gray-700">{{ loadingMessage }}</p>
            <div class="flex items-center justify-center space-x-1">
              <div class="h-2 w-2 animate-bounce rounded-full bg-blue-500" style="animation-delay: 0s"></div>
              <div class="h-2 w-2 animate-bounce rounded-full bg-blue-500" style="animation-delay: 0.1s"></div>
              <div class="h-2 w-2 animate-bounce rounded-full bg-blue-500" style="animation-delay: 0.2s"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- 错误状态 -->
      <div
        v-if="isError"
        class="error-state flex items-center justify-center border border-gray-200 rounded-lg bg-gray-50"
        :style="{ height: chartHeight }"
      >
        <div class="text-center">
          <div class="relative mb-4">
            <div
              class="mx-auto h-16 w-16 flex items-center justify-center border-2 border-gray-200 rounded-full bg-gray-100"
            >
              <icon-vue icon="mdi:graph-outline" class="h-8 w-8 text-gray-400" />
            </div>
          </div>
          <h3 class="mb-2 text-base font-medium text-gray-700">关联分析未完成</h3>
          <p class="text-sm leading-relaxed text-gray-500">
            需要重新分析才能生成关联图谱
            <br />
            <span class="text-xs text-gray-400">点击顶部"重新分析"按钮开始</span>
          </p>
        </div>
      </div>

      <!-- 图表容器 -->
      <div
        v-show="!showEmptyState && !isError"
        ref="chartContainer"
        class="chart-container border border-gray-200 rounded-lg bg-gray-50"
        :style="{ height: chartHeight }"
      ></div>

      <!-- 分析完成但无数据状态 -->
      <div
        v-if="!isLoading && !isError && !hasGraphData && status === 'completed'"
        class="no-data-state flex items-center justify-center border border-gray-200 rounded-lg bg-gray-50"
        :style="{ height: chartHeight }"
      >
        <div class="text-center">
          <div class="relative mb-4">
            <div
              class="mx-auto h-16 w-16 flex items-center justify-center border-2 border-gray-200 rounded-full bg-white"
            >
              <icon-vue icon="mdi:graph-outline" class="h-8 w-8 text-gray-400" />
            </div>
          </div>
          <h3 class="mb-2 text-base font-medium text-gray-700">暂无关联数据</h3>
          <p class="text-sm leading-relaxed text-gray-500">
            本次分析未发现代码与测试的关联关系
            <br />
            <span class="text-xs text-gray-400">可能是测试覆盖范围较小或关联度较低</span>
          </p>
        </div>
      </div>
    </div>

    <!-- 图例 -->
    <div
      v-if="hasGraphData && !isError && !isLoading"
      class="mt-4 flex items-center justify-center text-sm text-gray-500 space-x-8"
    >
      <div class="flex items-center">
        <div class="mr-2 h-3 w-3 rounded-full bg-blue-500"></div>
        <span>代码文件</span>
      </div>
      <div class="flex items-center">
        <div class="mr-2 h-3 w-3 rounded-full bg-green-500"></div>
        <span>测试用例</span>
      </div>
      <div class="flex items-center">
        <div class="mr-2 h-0.5 w-8 bg-blue-500"></div>
        <span>直接关联</span>
      </div>
      <div class="flex items-center">
        <div class="mr-2 h-0.5 w-8 bg-purple-500"></div>
        <span>间接关联</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { AnalysisStatus, CorrelationGraphData } from '@/types/precision-new/ai-analysis';
import { Icon as IconVue } from '@iconify/vue';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

// Props
interface Props {
  status: AnalysisStatus;
  graphData?: CorrelationGraphData;
  loading?: boolean;
  loadingMessage?: string;
  error?: string;
  height?: number;
}

const props = withDefaults(defineProps<Props>(), {
  graphData: undefined,
  loading: false,
  loadingMessage: '正在分析代码关联...',
  error: '',
  height: 400,
});

// Emits (保留定义但不使用，因为错误处理已集中化)
defineEmits<{
  retry: [];
}>();

// Refs
const chartContainer = ref<HTMLElement>();
let chart: any = null;
let resizeObserver: ResizeObserver | null = null;

// Computed
const isWaiting = computed(() => props.status === 'idle');
const isLoading = computed(() => props.loading || props.status === 'analyzing');
const isError = computed(() => props.status === 'error' || !!props.error);
const hasGraphData = computed(() => {
  const hasData = props.graphData && props.graphData.nodes && props.graphData.nodes.length > 0;
  console.log('CorrelationGraph: hasGraphData check', {
    graphData: props.graphData,
    hasNodes: props.graphData?.nodes?.length > 0,
    hasEdges: props.graphData?.edges?.length > 0,
    hasData,
  });
  return hasData;
});
const showEmptyState = computed(() => !isLoading.value && !isError.value && !hasGraphData.value);
const chartHeight = computed(() => `${props.height}px`);

// 动态导入 ECharts
async function loadECharts() {
  try {
    const echarts = await import('echarts');
    return echarts;
  } catch (error) {
    console.error('Failed to load ECharts:', error);
    return null;
  }
}

// 等待容器尺寸稳定
function waitForContainerSize(): Promise<void> {
  return new Promise((resolve) => {
    if (!chartContainer.value) {
      resolve();
      return;
    }

    const container = chartContainer.value;
    let attempts = 0;
    const maxAttempts = 20;

    const checkSize = () => {
      const rect = container.getBoundingClientRect();
      console.log('CorrelationGraph: container size check', {
        width: rect.width,
        height: rect.height,
        attempts: attempts + 1,
      });

      // 确保容器有合理的尺寸
      if (rect.width > 100 && rect.height > 100) {
        console.log('CorrelationGraph: container size ready');
        resolve();
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(checkSize, 100);
      } else {
        console.warn('CorrelationGraph: container size check timeout, proceeding anyway');
        resolve();
      }
    };

    checkSize();
  });
}

// 设置容器尺寸监听
function setupResizeObserver() {
  if (!chartContainer.value || !window.ResizeObserver) {
    return;
  }

  resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const { width, height } = entry.contentRect;
      console.log('CorrelationGraph: container resized', { width, height });

      if (chart && width > 0 && height > 0) {
        // 延迟执行resize，确保布局稳定
        setTimeout(() => {
          if (chart) {
            chart.resize();
            console.log('CorrelationGraph: chart resized due to container change');
          }
        }, 50);
      }
    }
  });

  resizeObserver.observe(chartContainer.value);
  console.log('CorrelationGraph: ResizeObserver setup complete');
}

// 初始化图表
async function initChart() {
  console.log('CorrelationGraph: initChart called', {
    hasContainer: !!chartContainer.value,
    containerElement: chartContainer.value,
  });

  if (!chartContainer.value) {
    console.log('CorrelationGraph: initChart failed - no container');
    return;
  }

  // 等待容器尺寸稳定
  await waitForContainerSize();

  const echarts = await loadECharts();
  if (!echarts) {
    console.log('CorrelationGraph: initChart failed - echarts load failed');
    return;
  }

  console.log('CorrelationGraph: initializing echarts chart');
  chart = echarts.init(chartContainer.value);
  chart.setOption(getEmptyChartOption());

  console.log('CorrelationGraph: chart initialized successfully', { chart: !!chart });

  // 确保图表尺寸正确
  setTimeout(() => {
    if (chart) {
      chart.resize();
      console.log('CorrelationGraph: initial resize completed');
    }
  }, 100);

  // 设置容器尺寸监听
  setupResizeObserver();

  // 添加窗口大小变化监听
  window.addEventListener('resize', handleResize);
}

// 获取空图表配置
function getEmptyChartOption(): any {
  return {
    title: {
      show: true,
      text: '暂无关联图谱数据',
      textStyle: { color: '#9CA3AF', fontSize: 14 },
      left: 'center',
      top: 'middle',
    },
    backgroundColor: '#F9FAFB',
    graphic: [] as any[],
    legend: { show: false },
    series: [] as any[],
  };
}

// 更新图表数据
function updateChart() {
  console.log('CorrelationGraph: updateChart called', {
    hasChart: !!chart,
    hasGraphData: hasGraphData.value,
    graphData: props.graphData,
    nodesCount: props.graphData?.nodes?.length,
    edgesCount: props.graphData?.edges?.length,
  });

  if (!chart || !props.graphData || !hasGraphData.value) {
    console.log('CorrelationGraph: updateChart early return', {
      hasChart: !!chart,
      hasGraphData: hasGraphData.value,
      hasProps: !!props.graphData,
    });
    return;
  }

  const { nodes, edges } = props.graphData;

  // 构建名称映射
  const idToNameMap: Record<string, string> = {};
  nodes.forEach((node) => {
    idToNameMap[node.id] = node.name;
  });

  // 转换节点数据
  const chartNodes = nodes.map((node: any) => {
    const normalizedSize = Math.max(1, Math.min(10, node.size || 5));
    const displaySize = 15 + (normalizedSize - 1) * 5;

    return {
      name: node.name,
      category: node.category === 'code' ? 0 : 1,
      symbolSize: displaySize,
      itemStyle: {
        color: node.category === 'code' ? '#3B82F6' : '#10B981',
        borderWidth: 0,
        shadowBlur: 6,
        shadowColor: 'rgba(0, 0, 0, 0.2)',
      },
      label: {
        show: true,
        position: 'inside',
        fontSize: Math.max(10, Math.min(14, 10 + normalizedSize * 0.4)),
        color: '#FFFFFF',
        fontWeight: 'bold',
        textShadowColor: 'rgba(0, 0, 0, 0.8)',
        textShadowBlur: 2,
        textShadowOffsetX: 1,
        textShadowOffsetY: 1,
      },
    };
  });

  // 转换边数据
  const chartLinks = edges.map((edge: any) => ({
    source: idToNameMap[edge.source] || edge.source,
    target: idToNameMap[edge.target] || edge.target,
    value: edge.weight || 1,
    lineStyle: {
      color: edge.type === 'direct' ? '#3B82F6' : '#8B5CF6',
      width: Math.max(4, (edge.weight || 1) * 6),
      opacity: 0.9,
      curveness: 0.3,
      type: 'solid',
    },
  }));

  // 图表配置
  const option: any = {
    title: { show: false },
    backgroundColor: '#F9FAFB',
    graphic: [] as any[],
    legend: {
      data: ['代码文件', '测试用例'],
      bottom: 15,
      itemWidth: 12,
      itemHeight: 12,
      textStyle: { color: '#6B7280', fontSize: 12 },
    },
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        if (params.dataType === 'node') {
          return `<b>${params.name}</b><br/>类型: ${params.data.category === 0 ? '代码文件' : '测试用例'}`;
        } else {
          return `<b>${params.data.source}</b> → <b>${params.data.target}</b><br/>关联类型: 直接关联`;
        }
      },
    },
    animation: true,
    animationDuration: 1500,
    animationEasingUpdate: 'quinticInOut',
    series: [
      {
        type: 'graph',
        layout: 'force',
        data: chartNodes,
        links: chartLinks,
        categories: [
          { name: '代码文件', itemStyle: { color: '#3B82F6' } },
          { name: '测试用例', itemStyle: { color: '#10B981' } },
        ],
        roam: true,
        focusNodeAdjacency: true,
        draggable: true,
        force: {
          repulsion: 800,
          gravity: 0.1,
          edgeLength: [150, 300],
          friction: 0.6,
          layoutAnimation: true,
        },
      },
    ] as any[],
  };

  console.log('CorrelationGraph: setting chart option', {
    chartNodes: chartNodes.length,
    chartLinks: chartLinks.length,
    option,
  });

  chart.setOption(option, true);

  // 确保图表正确渲染
  setTimeout(() => {
    if (chart) {
      chart.resize();
      console.log('CorrelationGraph: post-update resize completed');
    }
  }, 100);

  console.log('CorrelationGraph: chart option set successfully');
}

// 处理窗口大小变化
function handleResize() {
  if (chart) {
    chart.resize();
  }
}

// 强制刷新图表
function forceRefreshChart() {
  if (!chart || !hasGraphData.value) {
    return;
  }

  console.log('CorrelationGraph: force refresh chart');

  // 先resize再更新数据
  chart.resize();

  setTimeout(() => {
    if (chart && hasGraphData.value) {
      updateChart();
    }
  }, 50);
}

// 监听数据变化
watch(
  () => props.graphData,
  (newData) => {
    console.log('CorrelationGraph: graphData changed', {
      newData,
      hasGraphData: hasGraphData.value,
      status: props.status,
      loading: props.loading,
    });

    if (hasGraphData.value) {
      // 使用多重延迟确保渲染时机正确
      nextTick(() => {
        setTimeout(() => {
          if (!chart) {
            console.log('CorrelationGraph: chart not initialized, initializing now');
            initChart().then(() => {
              if (chart && hasGraphData.value) {
                // 再次延迟确保图表完全初始化
                setTimeout(() => {
                  updateChart();
                }, 150);
              }
            });
          } else {
            updateChart();
            // 确保图表尺寸正确
            setTimeout(() => {
              if (chart) {
                chart.resize();
                console.log('CorrelationGraph: data change resize completed');
              }
            }, 100);
          }
        }, 50);
      });
    } else if (chart) {
      chart.setOption(getEmptyChartOption(), true);
    }
  },
  { deep: true },
);

// 监听状态变化
watch(
  () => props.status,
  (newStatus) => {
    console.log('CorrelationGraph: status changed to', newStatus);

    if (newStatus === 'completed') {
      if (hasGraphData.value && chart) {
        // 分析完成且有数据时强制刷新
        setTimeout(() => {
          forceRefreshChart();
        }, 200);
      } else if (!hasGraphData.value && chart) {
        chart.setOption(getEmptyChartOption(), true);
      }
    }
  },
);

// 生命周期
onMounted(() => {
  nextTick(() => {
    initChart();
  });
});

onUnmounted(() => {
  // 清理ResizeObserver
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }

  // 清理图表
  if (chart) {
    chart.dispose();
    chart = null;
  }

  // 清理事件监听
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped>
.correlation-graph-card {
  height: 100%;
}

.chart-container {
  transition: all 0.3s ease;
}

.loading-overlay {
  backdrop-filter: blur(2px);
}

/* 动画效果 */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-4px);
  }
  60% {
    transform: translateY(-2px);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@keyframes ping {
  75%,
  100% {
    transform: scale(2);
    opacity: 0;
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

.animate-bounce {
  animation: bounce 2s infinite;
}

.animate-pulse {
  animation: pulse 2s infinite;
}

.animate-ping {
  animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
}
</style>
